/**
 * Shared model for supervisor timing/task authoring (post-sixth-demo, Leo
 * 2026-06-23). One model backs BOTH creation approaches behind a "Start from"
 * toggle, so flipping preserves the work:
 *
 *  - Start from days  — the working-hours picker is at the top; you place tasks
 *    within each day, organized day-by-day.
 *  - Start from tasks — the task list is at the top; you give each task its days
 *    and times, with the working-hours picker below.
 *
 * Either way there is ONE set of working-hours windows (owned by the picker /
 * `requested_schedule`) and ONE set of task allocations. A task allocation on a
 * day carries its own start/end CLOCK TIME (within the day's window) + crew +
 * lunch; man-hours = effective hours × crew. EVALUATION-only: the per-day task
 * detail isn't persisted yet (only the working-hours `requested_schedule`).
 */
import type { IntakeProposedTask, ScheduleDay } from '@vari-crew/schemas';
import { parseDay, toDayStr } from '@/lib/calendarGrid';

export type TimingLayout = 'day' | 'table';
export type TimingMode = 'days' | 'tasks';

export interface TimingTask {
  id: string;
  type: IntakeProposedTask['type'];
  name: string;
  description: string;
}

export const ROLE_KEYS = ['lead', 'installer', 'driver', 'trainee'] as const;
export type CrewRole = (typeof ROLE_KEYS)[number];
export type RoleCounts = Record<CrewRole, number>;
export const ROLE_LABELS: Record<CrewRole, string> = {
  lead: 'Lead',
  installer: 'Installer',
  driver: 'Driver',
  trainee: 'Trainee',
};
/** Compact labels for the in-row role chips. */
export const ROLE_ABBR: Record<CrewRole, string> = {
  lead: 'Lead',
  installer: 'Inst',
  driver: 'Driver',
  trainee: 'Trainee',
};
export const EMPTY_ROLES: RoleCounts = { lead: 0, installer: 0, driver: 0, trainee: 0 };

/** A task's allocation on a day: a start/end clock time (within the day's working
 *  hours) + a per-ROLE crew, optionally with lunch. All roles share this block's
 *  hours; man-hours and labor are computed from the role counts at the rates. */
export interface TimingAlloc {
  taskId: string;
  start: string; // 'HH:MM'
  end: string; // 'HH:MM'
  roles: RoleCounts;
  lunch: boolean;
}

export interface TimingDay {
  date: string; // 'YYYY-MM-DD'
  /** The day's working-hours window (owned by the working-hours picker). Task
   *  allocations fall within [start, end]. */
  window: { start: string; end: string };
  allocs: TimingAlloc[];
}

export interface TimingModel {
  days: TimingDay[];
  tasks: TimingTask[];
}

const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';

export function isWeekend(date: string): boolean {
  const dow = parseDay(date).getDay();
  return dow === 0 || dow === 6;
}

/** Calendar days in [start,end], weekends dropped unless opted in (Mon–Fri default). */
export function dayRange(
  start: string | null,
  end: string | null,
  includeWeekends: boolean,
): string[] {
  if (!start) return [];
  const last = end && end >= start ? end : start;
  const out: string[] = [];
  let d = parseDay(start);
  const lastD = parseDay(last);
  let guard = 0;
  while (d <= lastD && guard < 60) {
    out.push(toDayStr(d));
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    guard += 1;
  }
  if (includeWeekends) return out;
  const weekdays = out.filter((x) => !isWeekend(x));
  return weekdays.length > 0 ? weekdays : out;
}

const TYPE_META: Record<string, { label: string; bg: string; fg: string; bar: string }> = {
  pickup: { label: 'Pickup', bg: '#FAECE7', fg: '#712B13', bar: '#F0997B' },
  install: { label: 'Install', bg: '#EEEDFE', fg: '#3C3489', bar: '#AFA9EC' },
  punch: { label: 'Punch', bg: '#E1F5EE', fg: '#085041', bar: '#5DCAA5' },
  labor: { label: 'Labor', bg: '#F1EFE8', fg: '#444441', bar: '#B4B2A9' },
};
export const TASK_TYPE_OPTIONS: IntakeProposedTask['type'][] = [
  'pickup',
  'install',
  'punch',
  'labor',
];
export function typeMeta(type: string) {
  return TYPE_META[type] ?? { label: type, bg: '#F1EFE8', fg: '#444441', bar: '#B4B2A9' };
}

export function hoursBetween(start: string, end: string): number {
  const [sh = 0, sm = 0] = start.split(':').map(Number);
  const [eh = 0, em = 0] = end.split(':').map(Number);
  const mins = eh * 60 + em - (sh * 60 + sm);
  return mins > 0 ? mins / 60 : 0;
}
/** Clamp a 'HH:MM' time into [min, max] (no-op if the window is invalid). */
export function clampTime(t: string, min: string, max: string): string {
  if (max <= min) return t;
  if (t < min) return min;
  if (t > max) return max;
  return t;
}
const round1 = (n: number) => Math.round(n * 10) / 10;

export function allocHours(a: TimingAlloc): number {
  return hoursBetween(a.start, a.end);
}
export function allocEffHours(a: TimingAlloc): number {
  return Math.max(0, allocHours(a) - (a.lunch ? 1 : 0));
}
export function allocCrew(a: TimingAlloc): number {
  return ROLE_KEYS.reduce((s, r) => s + (a.roles[r] || 0), 0);
}
export function allocManHours(a: TimingAlloc): number {
  return round1(allocEffHours(a) * allocCrew(a));
}
/** Labor cost for one allocation = effective hours × Σ(role count × role rate). */
export function allocLaborCents(a: TimingAlloc, rates: Record<CrewRole, number>): number {
  const h = allocEffHours(a);
  return Math.round(ROLE_KEYS.reduce((s, r) => s + (a.roles[r] || 0) * h * (rates[r] || 0), 0));
}
export function totalLaborCents(m: TimingModel, rates: Record<CrewRole, number>): number {
  return m.days.reduce(
    (s, d) => s + d.allocs.reduce((ss, a) => ss + allocLaborCents(a, rates), 0),
    0,
  );
}
/** Per-role roll-up for the derived bid: man-hours + cost contributed by each role. */
export function laborBreakdown(
  m: TimingModel,
  rates: Record<CrewRole, number>,
): { rows: { role: CrewRole; manHours: number; cents: number }[]; manHours: number; cents: number } {
  const rows = ROLE_KEYS.map((role) => {
    let manHours = 0;
    let cents = 0;
    for (const d of m.days) {
      for (const a of d.allocs) {
        const c = a.roles[role] || 0;
        if (c > 0) {
          const h = allocEffHours(a);
          manHours += c * h;
          cents += c * h * (rates[role] || 0);
        }
      }
    }
    return { role, manHours: round1(manHours), cents: Math.round(cents) };
  }).filter((r) => r.manHours > 0);
  return {
    rows,
    manHours: round1(rows.reduce((s, r) => s + r.manHours, 0)),
    cents: rows.reduce((s, r) => s + r.cents, 0),
  };
}
/** Total task-time placed in the day (for the fit check vs the window). */
export function dayPlannedHours(d: TimingDay): number {
  return round1(d.allocs.reduce((s, a) => s + allocHours(a), 0));
}
export function dayManHours(d: TimingDay): number {
  return round1(d.allocs.reduce((s, a) => s + allocEffHours(a) * allocCrew(a), 0));
}
export function totalManHours(m: TimingModel): number {
  return round1(m.days.reduce((s, d) => s + dayManHours(d), 0));
}
export function windowHours(d: TimingDay): number {
  return round1(hoursBetween(d.window.start, d.window.end));
}

let seq = 0;
export function newTaskId(): string {
  seq += 1;
  return `tt${seq}`;
}

export function seedTasks(proposed?: IntakeProposedTask[]): TimingTask[] {
  if (proposed && proposed.length) {
    return proposed.map((t) => ({ id: newTaskId(), type: t.type, name: t.title, description: '' }));
  }
  // No proposed hint → start with ZERO tasks (demo #9, Leo). The old single
  // blank "install" skeleton seeded a phantom install task that rode through to
  // creation as a spurious extra task. The supervisor adds tasks via "Add task"
  // / "Suggest tasks"; in DAYS mode reconcileModel still mints a task per day.
  return [];
}

/**
 * Rebuild the model so its days cover exactly `dayKeys`, preserving existing
 * allocations and refreshing each day's window from the working-hours `schedule`.
 * In DAYS mode a brand-new day seeds one allocation filling the window; in TASKS
 * mode new days start empty (tasks are assigned to them explicitly).
 */
export function reconcileModel(
  prev: TimingModel,
  dayKeys: string[],
  schedule: ScheduleDay[] | null,
  mode: TimingMode,
): TimingModel {
  const prevByDate = new Map(prev.days.map((d) => [d.date, d]));
  const schedMap = new Map((schedule ?? []).map((s) => [s.date, s]));
  // Each days-mode block is its OWN task (labeled inline in its row). For a newly
  // added day, reuse a task no surviving day references (the initial seed, or an
  // orphan from a removed day) before minting a fresh one — so tasks track blocks
  // 1:1 without piling up as the range changes.
  const surviving = new Set(dayKeys);
  const referenced = new Set<string>(); // referenced by a SURVIVING day
  const everReferenced = new Set<string>(); // referenced by ANY prev day
  for (const d of prev.days) {
    for (const a of d.allocs) {
      everReferenced.add(a.taskId);
      if (surviving.has(d.date)) referenced.add(a.taskId);
    }
  }
  // Reuse only ORPHANS — a task a now-removed day used — never an unplaced
  // skeleton (a task no day ever referenced). Skeletons must stay in the
  // Unscheduled lane, not get silently adopted into a newly-added day's block.
  const reusable = prev.tasks
    .filter((t) => everReferenced.has(t.id) && !referenced.has(t.id))
    .map((t) => t.id);
  let reuseIdx = 0;
  const minted: TimingTask[] = [];
  const days: TimingDay[] = dayKeys.map((date) => {
    const s = schedMap.get(date);
    const window = { start: s?.start_time || DEFAULT_START, end: s?.end_time || DEFAULT_END };
    const prevDay = prevByDate.get(date);
    if (prevDay) {
      // Re-clamp existing allocations to the (possibly updated) window.
      return {
        date,
        window,
        allocs: prevDay.allocs.map((a) => ({
          ...a,
          start: clampTime(a.start, window.start, window.end),
          end: clampTime(a.end, window.start, window.end),
        })),
      };
    }
    if (mode !== 'days') return { date, window, allocs: [] };
    let taskId = reusable[reuseIdx];
    if (taskId) reuseIdx += 1;
    else {
      const t: TimingTask = { id: newTaskId(), type: 'install', name: '', description: '' };
      minted.push(t);
      taskId = t.id;
    }
    return {
      date,
      window,
      // No crew pre-filled — "no crew / no hours" is a first-class state now
      // (flexible task creation). The supervisor adds roles per block as needed.
      allocs: [{ taskId, start: window.start, end: window.end, roles: { ...EMPTY_ROLES }, lunch: false }],
    };
  });
  return { days, tasks: [...prev.tasks, ...minted] };
}

export function dayLabel(date: string): string {
  return parseDay(date).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// install/pickup tasks carry a NOT-NULL subtype (DB check constraints
// install_has_subtype / pickup_has_subtype). The studio doesn't author the
// subtype, so default it the way defaultTasksForKind does; the supervisor can
// refine it on the task editor. Other types have no subtype.
function defaultSubtype(type: TimingTask['type']): string | null {
  return type === 'install' ? 'new' : type === 'pickup' ? 'warehouse' : null;
}

/**
 * Flatten the authored model into persistable proposed tasks. A task with one or
 * more allocations emits ONE row per allocation-on-a-day (a multi-day task → one
 * row each), each carrying its placement (date + clock window → scheduled_start/
 * end on the server), the crew-facing description (→ tasks.description), the
 * scalar head count, and the per-role demand (→ tasks.crew_roles_needed).
 *
 * A task with NO allocation anywhere is a bare "skeleton" (the Unscheduled lane,
 * flexible task creation — Leo demo #7): it still emits ONE row with no
 * placement, no hours, and zero crew, so the supervisor can fill in the schedule
 * / crew / man-hours later. Blank names fall back to the type label so the title
 * is always valid; allocations whose task was deleted are skipped.
 */
export function modelToProposedTasks(m: TimingModel): IntakeProposedTask[] {
  const taskById = new Map(m.tasks.map((t) => [t.id, t]));
  const out: IntakeProposedTask[] = [];
  const placed = new Set<string>();
  for (const d of m.days) {
    for (const a of d.allocs) {
      const t = taskById.get(a.taskId);
      if (!t) continue;
      placed.add(t.id);
      const crew = allocCrew(a);
      const description = t.description.trim();
      out.push({
        type: t.type,
        subtype: defaultSubtype(t.type),
        title: t.name.trim() || typeMeta(t.type).label,
        crew_size_needed: Math.min(20, crew),
        estimated_duration_minutes: Math.round(allocHours(a) * 60),
        description: description || null,
        crew_roles_needed: crew > 0 ? { ...a.roles } : null,
        placement: { date: d.date, start_time: a.start, end_time: a.end },
      });
    }
  }
  // Skeleton tasks — never allocated to a day. Persist them bare (no schedule,
  // no crew, no hours) so they hold as not-yet-planned work, fillable later.
  for (const t of m.tasks) {
    if (placed.has(t.id)) continue;
    const description = t.description.trim();
    out.push({
      type: t.type,
      subtype: defaultSubtype(t.type),
      title: t.name.trim() || typeMeta(t.type).label,
      crew_size_needed: 0,
      estimated_duration_minutes: null,
      description: description || null,
      crew_roles_needed: null,
      placement: null,
    });
  }
  return out;
}
